import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { ProjectModal } from './ProjectModal';
import { useProjects } from '../lib/store';
import { Project } from '../lib/types';
function ProjectCard({
  project,
  index,
  onClick
}: {
  project: Project;
  index: number;
  onClick: () => void;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [hovered, setHovered] = useState(false);
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 60
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.15
  }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onClick={onClick} className="group border-t border-border py-12 md:py-16 cursor-pointer relative" data-id="element-339">
      <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-12 items-start" data-id="element-340">
        <span className="font-display text-6xl md:text-8xl font-bold text-secondary leading-none group-hover:text-primary transition-colors duration-500 w-[160px] md:w-[220px] shrink-0 block" data-id="element-341">
          {project.num}
        </span>

        <div className="space-y-4" data-id="element-342">
          <div className="flex items-baseline gap-4 flex-wrap" data-id="element-343">
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight group-hover:text-primary transition-colors duration-300" data-id="element-344">
              {project.title}
            </h3>
            <ArrowUpRightIcon className={`w-5 h-5 transition-all duration-300 ${hovered ? 'text-primary rotate-45' : 'text-muted-foreground'}`} data-id="element-345" />
          </div>

          <p className="font-mono text-xs text-muted-foreground tracking-widest" data-id="element-346">
            {project.role} · {project.year}
          </p>

          {project.summary && <p className="font-mono text-sm text-muted-foreground leading-relaxed max-w-lg" data-id="element-347">
              {project.summary}
            </p>}

          {project.metrics && project.metrics.length > 0 && <div className="flex flex-wrap gap-2 mt-4" data-id="element-348">
              {project.metrics.slice(0, 3).map(m => <span key={m} className="font-mono text-xs px-3 py-1.5 border border-border text-muted-foreground" data-id="element-349">
                  {m}
                </span>)}
            </div>}
        </div>

        {project.tools && project.tools.length > 0 && <div className="hidden md:block text-right" data-id="element-350">
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-351">
              Tech
            </span>
            <p className="font-mono text-xs text-foreground mt-1 whitespace-nowrap" data-id="element-352">
              {project.tools.map((t, i) => <span key={t} data-id="element-353">
                  {t}
                  {i < project.tools.length - 1 && <>
                      ,<br data-id="element-354" />
                    </>}
                </span>)}
            </p>
          </div>}
      </div>
    </motion.article>;
}
interface ProjectsSectionProps {
  initialSlug?: string;
}
export function ProjectsSection({
  initialSlug
}: ProjectsSectionProps) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const {
    projects,
    isLoading
  } = useProjects();
  const hasOpenedRef = useRef(false);
  const publishedProjects = projects.filter(p => p.status === 'published');
  const sortedProjects = [...publishedProjects].sort((a, b) => {
    return (a.order ?? 999) - (b.order ?? 999);
  });
  // Auto-open modal for deep link
  useEffect(() => {
    if (initialSlug && !isLoading && projects.length > 0 && !hasOpenedRef.current) {
      const found = projects.find(p => p.slug === initialSlug || p.id === initialSlug);
      if (found) {
        setSelectedProject(found);
        hasOpenedRef.current = true;
      }
    }
  }, [initialSlug, isLoading, projects]);
  if (isLoading) return null;
  return <>
      <section id="work" className="py-24 md:py-40 px-6 md:px-12 border-t border-border" data-id="element-355">
        <div className="max-w-6xl mx-auto" data-id="element-356">
          <div data-id="element-357">
            <span className="font-mono text-xs text-primary uppercase tracking-widest" data-id="element-358">
              // Selected Work
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter mt-4" data-id="element-359">
              Projects
            </h2>
          </div>

          <div ref={ref} className="mt-16" data-id="element-360">
            {sortedProjects.map((project, i) => <ProjectCard key={project.id} project={project} index={i} onClick={() => setSelectedProject(project)} data-id="element-361" />)}

            {sortedProjects.length === 0 && <p className="font-mono text-sm text-muted-foreground" data-id="element-362">
                No published projects yet.
              </p>}
          </div>
        </div>
      </section>

      <AnimatePresence data-id="element-363">
        {selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} data-id="element-364" />}
      </AnimatePresence>
    </>;
}